/** @prettier */
// @flow
import { RenderMode } from '@site-builder/common/src/types/build';
import {
  StoreUniversalItemType,
  GK_TYPE,
} from '@site-builder/common/src/utils/store';
import cn from 'classnames';
import React, { useEffect, useRef } from 'react';

import type { CustomButtonStyles } from '../../CallToAction';
import type { CustomButtonComponent } from '@site-builder/common/src/flow-types/components';
import type { Sku } from '@site-builder/common/src/flow-types/sku';
import type { FiveSymbolLocale } from '@site-builder/common/src/utils/locale';

import { isSandboxMode } from '../../../../scripts/helpers/sandbox';
import { injectBuyButtonHook } from '../../../../utils/hooks/injectBuyButtonHook';
import { BuyButtonWidgetOptionsBuilder } from '../../../../utils/services/buyButtonWidget';

type Props = {|
  button: CustomButtonComponent,
  buttonId: string,
  blockType: string,
  onClick: () => void,
  skus: Sku[],
  isStoreDataLoading: boolean,
  renderMode: RenderMode,
  isServerSideRender: boolean,
  isNewScheme: boolean,
  projectId: string,
  analyticsCounterId: number,
  locale: FiveSymbolLocale,
  theme: CustomButtonStyles,
|};

const GameKeysButton = ({
  button,
  buttonId,
  blockType,
  onClick,
  skus,
  isStoreDataLoading,
  renderMode,
  isServerSideRender,
  isNewScheme,
  projectId,
  analyticsCounterId,
  locale,
  theme,
}: Props) => {
  const sku = button.value.buy.id;

  const buttonRef = useRef(null);
  useEffect(() => {
    if (!sku) {
      return;
    }

    const widgetOptions = new BuyButtonWidgetOptionsBuilder({
      projectId,
      selector: `#${buttonId}`,
      analyticsCounterId,
      locale,
    })
      .withSku(sku)
      .withItemType(
        isNewScheme
          ? StoreUniversalItemType.UNIT
          : StoreUniversalItemType.DIGITAL_CONTENT
      )
      .withSandbox(isSandboxMode(renderMode))
      .construct();

    // eslint-disable-next-line consistent-return
    return injectBuyButtonHook({
      widgetOptions,
      buttonRef,
      onClick,
      isServerSideRender,
    });
  }, [
    projectId,
    buttonId,
    analyticsCounterId,
    locale,
    sku,
    isNewScheme,
    renderMode,
    isServerSideRender,
    onClick,
    buttonRef,
    theme,
  ]);

  let buttonText = '';
  if (!sku) {
    buttonText = button.label[locale];
  }
  const foundSku = skus.find((sku) => sku.name === sku);
  const isEditing = renderMode === RenderMode.EDITING;
  if (isEditing && sku && button.subtype === 'buy' && !foundSku) {
    buttonText = button.unavailableBuyButtonLabel[locale];
  }

  const dataAttributes = {
    'data-button-type': GK_TYPE, // attribute is used to find button handler in landing
    'data-button-sku': sku, // data-button-sku is used to render Xsolla Buy Button in landing
  };

  return (
    <div
      id={buttonId} // buy-button mounts here, inner content not render properly (potential bug)
      className='ui-site-calltoaction ui-site-calltoaction--buy'
      style={theme} // TODO move to css-vars theme
      ref={buttonRef}
      role='button'
      tabIndex={0}
      onClick={onClick}
      onKeyDown={onClick}
      {...(isServerSideRender ? dataAttributes : {})} // if isServerSideRender is enabled, than handlers located in client/src/scripts/helpers/cta.js used, else used handlers located in this component (hooks)
      data-xa-method='elementClick' // xa attributes used for sending events to Xsolla Metrika
      data-xa-name='sku_button'
      data-xa-extra={JSON.stringify({
        id: sku,
        name: foundSku?.name,
        title: foundSku?.title,
        block: blockType,
      })}
    >
      {/* TODO separate styles of the component from 'ui-site-calltoaction' */}
      <div
        className={cn('ui-site-calltoaction__text', {
          'is-preloader': isStoreDataLoading,
        })}
      >
        {buttonText}
      </div>
    </div>
  );
};

export default GameKeysButton;
